// src/pages/TagPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import api, { LANG_KEY } from "../lib/api";

import ArticleList from "../components/ArticleList";
import CategoryList from "../components/CategoryList";
import TagList from "../components/TagList";
const API_BASE = process.env.REACT_APP_API_BASE || "http://192.168.1.89:8000/api";

const TagPage = () => {
  const { slug } = useParams();
   const [lang, setLang] = useState(() => {
      try {
        return localStorage.getItem(STORAGE_LANG_KEY) || "en";
      } catch {
        return "en";
      }
    });
    const STORAGE_LANG_KEY = LANG_KEY || "site_lang";
    const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
  const [tag, setTag] = useState(null);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function fetchData() {
      setLoading(true);
      setErr(null);
      try {
        const [tagRes, articlesRes, catsRes, tagsRes] = await Promise.all([
          axios.get(`${API_BASE}/tags/${slug}/`, { signal: controller.signal }),
          axios.get(`${API_BASE}/tags/${slug}/articles/`, { signal: controller.signal }),
          axios.get(`${API_BASE}/categories/`, { signal: controller.signal }),
          axios.get(`${API_BASE}/tags/`, { signal: controller.signal }),
        ]);

        if (cancelled) return;

        const norm = (d) =>
          Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];

        setTag(tagRes.data ?? null);
        setArticles(norm(articlesRes.data));
        setCategories(norm(catsRes.data));
        setTags(norm(tagsRes.data));
      } catch (e) {
        if (axios.isCancel(e)) return;
        if (!cancelled) {
          setErr(e);
          setTag(null);
          setArticles([]);
          setCategories([]);
          setTags([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    window.scrollTo({ top: 0, behavior: "smooth" });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [slug]);

  const articleCount = useMemo(() => articles.length, [articles]);
  const accent = tag?.color || "#0d6efd";
  const pageTitle = tag ? `#${tag.name} | 24News` : "Tag | 24News";
  const pageDescription =
    tag?.description ||
    (tag ? `Articles tagged with #${tag.name} on 24News.` : "Browse tagged articles on 24News.");

  if (loading) {
    return (
      <div className="container py-5">
        <div className="placeholder-glow">
          <div
            className="rounded-4 mb-4"
            style={{ height: 160, background: "var(--bs-secondary-bg)", opacity: 0.4 }}
          />
          <div className="row g-4">
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                  <span className="placeholder col-6 d-block mb-2"></span>
                  <span className="placeholder col-8 d-block mb-2"></span>
                  <span className="placeholder col-4 d-block"></span>
                </div>
              </div>
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <span className="placeholder col-5 d-block mb-2"></span>
                  <span className="placeholder col-7 d-block mb-2"></span>
                  <span className="placeholder col-3 d-block"></span>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                  <span className="placeholder col-4 d-block mb-2"></span>
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="placeholder col-8 d-block mb-2"></span>
                  ))}
                </div>
              </div>
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <span className="placeholder col-4 d-block mb-2"></span>
                  {[...Array(6)].map((_, i) => (
                    <span key={i} className="placeholder col-6 d-block mb-2"></span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          <strong>Erreur :</strong> {err.message}
        </div>
      </div>
    );
  }

  if (!tag) {
    return (
      <div className="container py-5">
        <h2>Tag not found</h2>
        <p>The tag “{slug}” doesn’t exist.</p>
        <Link to="/" className="btn btn-primary">
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: "en",
          dir: "ltr",
        }}
      >
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:site_name" content="24News" />
        {tag?.color ? <meta property="og:image" content={tag.color} /> : null}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {tag?.color ? <meta name="twitter:image" content={tag.color} /> : null}
         <link rel="canonical" href={`${window.location.origin}/${lang}/tags/${slug}`} />
      </Helmet>

      <div className="container py-5">
        <section
          className="rounded-4 p-4 p-lg-5 mb-4 position-relative overflow-hidden"
          style={{
            background:
              "linear-gradient(90deg, rgba(13,110,253,.06), rgba(13,110,253,.02))",
          }}
        >
          <div
            className="position-absolute top-0 end-0 opacity-25"
            style={{
              width: 220,
              height: 220,
              background: `radial-gradient(circle at 70% 30%, ${accent}33, transparent 60%)`,
              transform: "translate(40px,-40px)",
            }}
          />
          <nav className="mb-3" aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/" className="text-decoration-none">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Tag
              </li>
            </ol>
          </nav>

          <h1 className="display-5 fw-bold mb-2">
            Tag: <span style={{ color: accent }}>#{tag.name}</span>
          </h1>
          {tag.description ? (
            <p className="lead text-body-secondary mb-4">{tag.description}</p>
          ) : (
            <p className="lead text-body-secondary mb-4">
              Articles tagged with <strong>#{tag.name}</strong>.
            </p>
          )}

          <div className="d-flex flex-wrap gap-2 align-items-center">
            <span className="badge text-bg-light border">
              {articleCount} article{articleCount !== 1 ? "s" : ""}
            </span>
            <button type="button" className="btn btn-primary btn-sm">
              Follow tag
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigator.clipboard.writeText(window.location.href)}
            >
              <i className="bi bi-link-45deg me-1" />
              Copy link
            </button>
          </div>
        </section>

        <div className="row g-4">
          <main className="col-lg-8">
            {articles.length > 0 ? (
              <ArticleList articles={articles} />
            ) : (
              <div className="alert alert-info">No articles found for this tag.</div>
            )}
          </main>

          <aside className="col-lg-4">
            <div className="sticky-top" style={{ top: 92 }}>
              <section className="card shadow-sm mb-4">
                <div className="card-body">
                  <h5 className="card-title">Categories</h5>
                  <CategoryList categories={categories} />
                </div>
              </section>

              <section className="card shadow-sm mb-4">
                <div className="card-body">
                  <h5 className="card-title">All Tags</h5>
                  <TagList tags={tags} />
                </div>
              </section>

            </div>
          </aside>
        </div>
      </div>
    </>
  );
};

export default TagPage;
